// תוצאת יצוא: חבילה, משקלים, checksum, גרסאות, בדיקות, הורדה, דוח והעלאה לדמו.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { ErrorBox, fmtDate, fmtKb, useToast } from '../ui';
import type { Check } from '../editor/types';
import ChecksView from './ChecksView';

export interface ExportRow {
  id: string; variant_id: string; banner_revision: number; kind: 'delivery' | 'draft' | 'test'; status: 'building' | 'ready' | 'blocked' | 'failed';
  package_name: string; checksum: string | null; size_bytes: number | null; unzipped_bytes: number | null; resolved_url: string | null;
  renderer_version: string; spec_version_id: string; brand_version_id: string; created_at: string;
  check_results?: Check[]; manifest?: any; warnings_ack?: { codes: string[]; userName?: string; at?: string } | null; summary?: { errors: number; warnings: number; passed: number };
  hasZip?: boolean; hasBackup?: boolean; jobStatus?: string | null;
}

export const KIND_LABEL: Record<string, string> = { delivery: 'מסירה', draft: 'טיוטה', test: 'חבילת טסט' };
export const XSTATUS: Record<string, [string, string]> = { building: ['בבנייה', 'info'], ready: ['מוכן', 'ok'], blocked: ['חסום', 'err'], failed: ['נכשל', 'err'] };

async function openSigned(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function ExportResult({ e }: { e: ExportRow }) {
  const toast = useToast();
  const [err, setErr] = useState<string | null>(null);
  const [demo, setDemo] = useState<{ id: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const m = e.manifest ?? {};
  const v = m.versions ?? {};
  const [st, cls] = XSTATUS[e.status] ?? [e.status, ''];

  const download = async (which: 'zip' | 'backup') => {
    setErr(null);
    try {
      const r = await api.get<{ url: string; backupUrl: string | null }>(`/api/exports/${e.id}/download`);
      const u = which === 'zip' ? r.url : r.backupUrl;
      if (!u) { setErr('אין קובץ כזה ליצוא זה'); return; }
      await openSigned(u);
    } catch (x: any) { setErr(x.message); }
  };
  const report = async () => {
    setErr(null);
    try { const r = await api.get<{ url: string }>(`/api/exports/${e.id}/report`); await openSigned(r.url); } catch (x: any) { setErr(x.message); }
  };
  const toDemo = async () => {
    setBusy(true); setErr(null);
    try { const p = await api.post<{ id: string }>('/api/demo-packages/from-export', { exportId: e.id }); setDemo(p); toast('החבילה הועלתה לאתר הדמו'); } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  };

  return (
    <div className="pgroup" aria-label={`יצוא ${e.package_name}`}>
      <div className="row between">
        <div className="row">
          <span className={`badge ${cls}`}>{st}</span>
          <span className="badge">{KIND_LABEL[e.kind] ?? e.kind}</span>
          {e.kind !== 'delivery' && <span className="badge warn">מסומן: {KIND_LABEL[e.kind]} — לא למסירה</span>}
          <strong className="mono" style={{ wordBreak: 'break-all' }}>{e.package_name}</strong>
        </div>
        <span className="small muted">{fmtDate(e.created_at)}</span>
      </div>
      <dl className="kv" style={{ marginTop: 6 }}>
        <dt>ZIP</dt><dd className="mono">{e.size_bytes != null ? fmtKb(e.size_bytes) : '—'}</dd>
        <dt>לאחר חילוץ</dt><dd className="mono">{e.unzipped_bytes != null ? fmtKb(e.unzipped_bytes) : '—'}</dd>
        <dt>checksum (SHA-256)</dt><dd className="mono small">{e.checksum ?? '—'}</dd>
        <dt>גרסאות</dt>
        <dd className="small">
          באנר <span className="mono">r{e.banner_revision}</span> · מותג <span className="mono">{v.brandVersionId ?? e.brand_version_id}</span> · מפרט <span className="mono">v{v.specVersion ?? '?'}</span> · renderer <span className="mono">{v.rendererVersion ?? e.renderer_version}</span>
          {v.trackingTemplateVersion != null && <> · תבנית תיוג <span className="mono">v{v.trackingTemplateVersion}</span></>}
        </dd>
        <dt>קישור נעול ביצוא</dt><dd className="mono small" style={{ wordBreak: 'break-all' }}>{m.resolvedUrl ?? e.resolved_url ?? '—'}</dd>
        {m.productionUrl && m.productionUrl !== m.resolvedUrl && <><dt>קישור production</dt><dd className="mono small">{m.productionUrl}</dd></>}
        {m.clickAdapter && <><dt>click adapter</dt><dd className="small">{m.clickAdapter.name ?? '—'} {m.clickAdapter.isDemo ? '(הדגמה)' : ''}</dd></>}
        {e.warnings_ack?.codes?.length ? <><dt>אזהרות שאושרו</dt><dd className="small"><span className="mono">{e.warnings_ack.codes.join(', ')}</span>{e.warnings_ack.userName ? ` · ${e.warnings_ack.userName}` : ''}{e.warnings_ack.at ? ` · ${fmtDate(e.warnings_ack.at)}` : ''}</dd></> : null}
        {m.backupImage && <><dt>תמונת גיבוי</dt><dd className="small mono">{m.backupImage.file} (מחוץ ל-ZIP)</dd></>}
      </dl>
      {m.gate?.blocked && <div className="alert err small">היצוא נחסם: {(m.gate.reasons ?? []).join(' · ')}</div>}
      {e.check_results && e.check_results.length > 0 && <details style={{ marginTop: 6 }}><summary className="small">תוצאות בדיקה ({e.summary?.errors ?? 0} שגיאות · {e.summary?.warnings ?? 0} אזהרות · {e.summary?.passed ?? 0} עברו)</summary><ChecksView checks={e.check_results} /></details>}
      <div className="row" style={{ marginTop: 8 }}>
        <button className="btn small primary" disabled={e.status !== 'ready'} onClick={() => download('zip')}>הורדת ZIP</button>
        {e.hasBackup && <button className="btn small" onClick={() => download('backup')}>תמונת גיבוי</button>}
        <button className="btn small" disabled={e.status === 'building'} onClick={report}>דוח בדיקות + manifest</button>
        <button className="btn small" disabled={e.status !== 'ready' || busy} onClick={toDemo}>העלה לדמו</button>
        {demo && <Link className="small" to="/demo">לאתר הדמו ←</Link>}
      </div>
      <ErrorBox error={err} />
    </div>
  );
}
